import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import {
    ShoppingBag, ChevronLeft, ChevronRight, Loader2, Package,
    Clock, CheckCircle2, Ban, FileText, RefreshCw,
} from 'lucide-react';

import PortalLayout from './PortalLayout';
import { Badge } from '../../components/ui/badge';
import { portalOrdersAPI } from '../../lib/portalApi';
import { usePortalCart } from '../../context/PortalCartContext';

const STATUS_META = {
    pending_review: { Icon: Clock,        cls: 'bg-amber-50 text-amber-800 border-amber-200' },
    approved:       { Icon: CheckCircle2, cls: 'bg-emerald-50 text-emerald-800 border-emerald-200' },
    rejected:       { Icon: Ban,          cls: 'bg-red-50 text-red-800 border-red-200' },
    converted:      { Icon: CheckCircle2, cls: 'bg-emerald-50 text-emerald-800 border-emerald-200' },
};

const formatEur = (v) =>
    new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(Number(v) || 0);

const PortalOrders = () => {
    const { t } = useTranslation('portal');
    const navigate = useNavigate();
    const { refresh: refreshCart } = usePortalCart();
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [expanded, setExpanded] = useState(null);
    // Per-row reorder busy state so users can't double-click and duplicate
    // the merge, and so we don't lock the entire page during the request.
    const [reorderingId, setReorderingId] = useState(null);

    /**
     * Clone a past order into the customer's active cart. Merges by
     * product_id (SET semantics — repeated clicks don't compound), skips
     * out-of-stock / removed items, and surfaces a rich toast summary so
     * the customer knows exactly what landed vs what was skipped.
     */
    const handleReorder = async (order, e) => {
        e?.stopPropagation();   // don't toggle the accordion when clicking the button
        setReorderingId(order.id);
        try {
            const { data } = await portalOrdersAPI.reorder(order.id);
            const addedN = (data.added || []).length;
            const oosN = (data.skipped_out_of_stock || []).length;
            const removedN = (data.skipped_removed || []).length;
            const adjustedN = (data.adjusted_moq || []).length;

            if (addedN > 0) {
                const parts = [
                    t('orders.reorder.added_toast', {
                        count: addedN,
                        defaultValue_one: '{{count}} item added to your cart',
                        defaultValue_other: '{{count}} items added to your cart',
                    }),
                ];
                if (oosN > 0)      parts.push(t('orders.reorder.skipped_oos', { count: oosN, defaultValue: '{{count}} skipped (out of stock)' }));
                if (removedN > 0)  parts.push(t('orders.reorder.skipped_removed', { count: removedN, defaultValue: '{{count}} no longer available' }));
                if (adjustedN > 0) parts.push(t('orders.reorder.adjusted_moq', { count: adjustedN, defaultValue: '{{count}} qty rounded up to MOQ' }));
                toast.success(parts.join(' · '), { duration: 6000, action: {
                    label: t('orders.reorder.view_cart', { defaultValue: 'View cart' }),
                    onClick: () => navigate('/portal/cart'),
                }});
            } else if (oosN + removedN > 0) {
                toast.warning(t('orders.reorder.nothing_available', {
                    defaultValue: 'None of the items in this order can be reordered right now (out of stock or unavailable).',
                }));
            } else {
                toast(t('orders.reorder.empty', { defaultValue: 'Nothing to reorder — this order has no eligible line items.' }));
            }
            await refreshCart();
        } catch (err) {
            toast.error(t('orders.reorder.failed', { defaultValue: 'Could not reorder — please try again.' }));
        } finally {
            setReorderingId(null);
        }
    };

    useEffect(() => {
        let alive = true;
        portalOrdersAPI.list()
            .then(({ data }) => { if (alive) setItems(data.items || []); })
            .catch(() => { if (alive) setItems([]); })
            .finally(() => { if (alive) setLoading(false); });
        return () => { alive = false; };
    }, []);

    return (
        <PortalLayout>
            <div className="space-y-6" data-testid="portal-orders-page">
                <button
                    onClick={() => navigate(-1)}
                    className="text-sm text-gray-500 hover:text-[#1A1A1A] flex items-center gap-1"
                    data-testid="portal-orders-back-btn"
                >
                    <ChevronLeft size={16} /> {t('common.back')}
                </button>

                <div>
                    <p className="text-xs uppercase tracking-[3px] text-[#C08478] font-semibold mb-2">{t('orders.section_label')}</p>
                    <h1 className="text-3xl font-bold text-[#1A1A1A] mb-1 flex items-center gap-2">
                        <ShoppingBag size={26} className="text-[#C08478]" /> {t('orders.title')}
                    </h1>
                    <p className="text-sm text-gray-500">{t('orders.subtitle')}</p>
                </div>

                {loading ? (
                    <div className="flex items-center justify-center py-20 text-gray-400">
                        <Loader2 size={20} className="animate-spin mr-2" /> {t('orders.loading')}
                    </div>
                ) : items.length === 0 ? (
                    <div className="bg-white border border-[#EFE5E1] rounded-lg p-12 text-center" data-testid="portal-orders-empty">
                        <Package size={32} className="mx-auto text-gray-300 mb-3" />
                        <h3 className="font-semibold text-[#1A1A1A] mb-1">{t('orders.empty_title')}</h3>
                        <p className="text-sm text-gray-500 mb-4">{t('orders.empty_body')}</p>
                        <button
                            onClick={() => navigate('/portal/catalog')}
                            data-testid="portal-orders-browse-btn"
                            className="text-sm bg-[#1A1A1A] hover:bg-[#333] text-white rounded-full px-5 py-2 inline-flex items-center gap-1.5"
                        >
                            {t('orders.browse_cta')} <ChevronRight size={14} />
                        </button>
                    </div>
                ) : (
                    <div className="space-y-3">
                        {items.map((o) => {
                            const meta = STATUS_META[o.status] || STATUS_META.pending_review;
                            const Icon = meta.Icon;
                            const isOpen = expanded === o.id;
                            return (
                                <div
                                    key={o.id}
                                    className="bg-white border border-[#EFE5E1] rounded-lg overflow-hidden hover:shadow-sm transition-shadow"
                                    data-testid={`portal-order-card-${o.order_number}`}
                                >
                                    <button
                                        type="button"
                                        onClick={() => setExpanded(isOpen ? null : o.id)}
                                        className="w-full px-5 py-4 flex flex-wrap items-center justify-between gap-3 text-left"
                                        data-testid={`portal-order-toggle-${o.order_number}`}
                                    >
                                        <div className="flex items-center gap-3 flex-wrap">
                                            <span className="font-mono font-semibold text-[#1A1A1A] text-sm">{o.order_number}</span>
                                            <Badge variant="outline" className={`${meta.cls} font-medium`}>
                                                <Icon size={11} className="mr-1" />
                                                {t(`orders.status.${o.status}`, { defaultValue: o.status })}
                                            </Badge>
                                            {o.order_type === 'quotation' && (
                                                <Badge variant="outline" className="bg-violet-50 text-violet-700 border-violet-200 text-[10px] uppercase tracking-wider">
                                                    <FileText size={10} className="mr-1" /> {t('orders.type_quotation')}
                                                </Badge>
                                            )}
                                            {o.quotation_number && (
                                                <span className="text-[11px] text-emerald-700 font-mono">→ {o.quotation_number}</span>
                                            )}
                                        </div>
                                        <div className="flex items-center gap-4 text-xs text-gray-500">
                                            <span>{o.item_count} {t('orders.items')}</span>
                                            <span className="font-semibold text-[#1A1A1A]">{formatEur(o.subtotal)}</span>
                                            <span>{o.created_at?.slice(0, 16).replace('T', ' ')}</span>
                                            {/* Reorder — B2B repeat-buy shortcut. Rendered here so
                                                the customer doesn't need to expand the row first. */}
                                            <span
                                                role="button"
                                                tabIndex={0}
                                                data-testid={`portal-order-reorder-${o.order_number}`}
                                                aria-label={t('orders.reorder.aria', { defaultValue: 'Reorder items from {{n}}', n: o.order_number })}
                                                onClick={(e) => handleReorder(o, e)}
                                                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); handleReorder(o, e); } }}
                                                className={`inline-flex items-center gap-1.5 text-[11px] font-medium px-2.5 py-1 rounded-full border transition ${
                                                    reorderingId === o.id
                                                        ? 'bg-gray-100 text-gray-400 border-gray-200 cursor-wait'
                                                        : 'bg-[#FBF8F6] border-[#EFE5E1] text-[#C08478] hover:bg-[#C08478] hover:text-white hover:border-[#C08478] cursor-pointer'
                                                }`}
                                            >
                                                {reorderingId === o.id
                                                    ? <Loader2 size={11} className="animate-spin" />
                                                    : <RefreshCw size={11} />}
                                                {t('orders.reorder.button', { defaultValue: 'Reorder' })}
                                            </span>
                                            <ChevronRight size={14} className={`transition-transform ${isOpen ? 'rotate-90' : ''}`} />
                                        </div>
                                    </button>
                                    {isOpen && (
                                        <div className="px-5 pb-4 border-t border-[#F5EEEA] bg-[#FBF8F6]" data-testid={`portal-order-details-${o.order_number}`}>
                                            <table className="w-full text-sm mt-3">
                                                <thead className="text-[10px] uppercase tracking-[2px] text-gray-500">
                                                    <tr>
                                                        <th className="text-left py-2">{t('orders.col_sku')}</th>
                                                        <th className="text-left py-2">{t('orders.col_product')}</th>
                                                        <th className="text-right py-2">{t('orders.col_qty')}</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {(o.items || []).map((it) => (
                                                        <tr key={it.product_id} className="border-t border-[#EFE5E1]">
                                                            <td className="py-1.5 font-mono text-[11px] text-gray-500">{it.sku}</td>
                                                            <td className="py-1.5">{it.name}</td>
                                                            <td className="py-1.5 text-right tabular-nums">{it.qty}</td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                            {o.reject_reason && (
                                                <p className="mt-3 text-xs text-red-700">
                                                    <strong>{t('orders.reject_reason')}:</strong> {o.reject_reason}
                                                </p>
                                            )}
                                            {o.notes && (
                                                <p className="mt-3 text-xs text-gray-500">
                                                    <strong>{t('orders.your_notes')}:</strong> {o.notes}
                                                </p>
                                            )}
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>
        </PortalLayout>
    );
};

export default PortalOrders;
